import { pick } from 'lodash-es';

const WIRE_FIELDS = [
  'name',
  'message',
  'code',
  'type',
  'status',
  'data',
  'stacktrace',
] as const;

/** The fields of a value raised by an op handler that cross the port. */
export type OpErrorWire = Partial<
  Record<(typeof WIRE_FIELDS)[number], unknown>
>;

const MAX_WRAPPED_DEPTH = 3;

/** The errors a disposal, aggregate or caused error carries, original first. */
function wrappedErrors(raised: object): unknown[] {
  const { suppressed, error, errors, cause } = raised as Record<
    string,
    unknown
  >;
  return [
    suppressed,
    error,
    ...(Array.isArray(errors) ? errors : []),
    cause,
  ].filter(value => value !== undefined && value !== null);
}

/** A raised value in words: its name and message, or what kind of value it is. */
function describeRaised(raised: unknown, depth = 0): string {
  if (raised === null || typeof raised !== 'object') return String(raised);
  const { name, message, type } = raised as Record<string, unknown>;
  const head =
    [name, message]
      .filter(part => typeof part === 'string' && part)
      .join(': ') ||
    [Object.prototype.toString.call(raised), type]
      .filter(part => typeof part === 'string' && part)
      .join(' ');
  if (depth >= MAX_WRAPPED_DEPTH) return head;
  const wrapped = wrappedErrors(raised).map(value =>
    describeRaised(value, depth + 1)
  );
  return wrapped.length ? `${head} (${wrapped.join('; ')})` : head;
}

/**
 * The wire form of a value an op handler raised. The message always says what
 * was raised: a handler may raise a plain value such as an abort reason, and
 * a disposal error hides the failure it wraps.
 */
export function toOpErrorWire(raised: unknown): OpErrorWire {
  if (raised === null || typeof raised !== 'object') {
    return { name: 'Error', message: `Raised value: ${String(raised)}` };
  }
  const wire: OpErrorWire = pick(raised, WIRE_FIELDS);
  const { stack } = raised as { stack?: unknown };
  if (wire.stacktrace === undefined && typeof stack === 'string') {
    wire.stacktrace = stack;
  }
  const own = typeof wire.message === 'string' ? wire.message : '';
  const wrapped = wrappedErrors(raised).map(value => describeRaised(value, 1));
  if (wrapped.length) {
    wire.message = [own, `(${wrapped.join('; ')})`].filter(Boolean).join(' ');
  } else if (!own && !(typeof wire.name === 'string' && wire.name)) {
    wire.message = `Raised value: ${describeRaised(raised)}`;
  }
  return wire;
}

/** The error the calling side raises for a wire error of the handler side. */
export function fromOpErrorWire(wire: OpErrorWire | undefined): Error {
  const error = new Error(
    typeof wire?.message === 'string' ? wire.message : undefined
  );
  Object.assign(error, wire);
  if (typeof wire?.stacktrace === 'string') {
    error.stack = `${error.stack}\n    raised by the op handler:\n${wire.stacktrace}`;
  }
  return error;
}
